import { useCallback, useEffect, useRef } from "react"
import { Link, useNavigate, useSearch } from "@tanstack/react-router"
import {
	IconChartActivityOutline18 as Activity,
	IconTriangleWarningOutline18 as AlertTriangle,
	IconArrowRightOutline18 as ArrowRight,
	IconCreditCardOutline18 as CreditCard,
} from "nucleo-ui-outline-18"

import type { Plan } from "@productlane/lib/src/browser"
import {
	hasLegacyPlan,
	PLAN_DISPLAY_NAMES,
	STARTUP_PROGRAM_CONTRACT_LENGTH_MONTHS,
	STARTUP_PROGRAM_DISPLAY_NAME,
} from "@productlane/lib/src/browser"
import { Alert, Button, Pill } from "@productlane/ui"

import { MetaInfo } from "@/components/meta-info"
import { api } from "@/lib/api"
import { captureError } from "@/lib/capture-error"
import { trackPaidSubscriptionConversion } from "@/lib/google-ads"
import { useStartTrial } from "@/lib/use-start-trial"
import { useTrial } from "@/lib/useTrial"
import { useZeroWorkspace } from "@/lib/zero"
import {
	BillingDetailsSection,
	cardClass,
	RecentInvoicesSection,
} from "../plans/billing-details"
import { PaymentIssueAlert } from "../plans/payment-issue-alert"
import { nextUpgradeTier, UpgradeCard } from "../plans/upgrade-card"
import { SettingsHeader } from "../settings-header"
import { SETTINGS_INSET_CLASS } from "../settings-switches/settings-container"
import { AiSettingsRow } from "./ai-settings-row"

const formatRenewalDate = (iso: string) =>
	new Date(iso).toLocaleDateString(undefined, {
		month: "short",
		day: "numeric",
	})

const formatMoney = (amountInCents: number, currency: string) =>
	new Intl.NumberFormat(undefined, {
		style: "currency",
		currency: currency.toUpperCase(),
		minimumFractionDigits: amountInCents % 100 === 0 ? 0 : 2,
	}).format(amountInCents / 100)

interface CurrentPlanCardProps {
	activePlan: Plan
}

const CurrentPlanCard = ({ activePlan }: CurrentPlanCardProps) => {
	const workspace = useZeroWorkspace()
	const { data: trialInfo } = api.workspace.trialInfo.useQuery(undefined, {
		cacheTime: Infinity,
		staleTime: Infinity,
	})
	const { daysLeft, isTrial, cancelAtPeriodEnd } = useTrial()
	const effectivePlan: Plan = trialInfo?.plan ?? activePlan
	const needsUpgrade = effectivePlan !== "free" && hasLegacyPlan(effectivePlan)

	const createManagePlansMutation = api.workspace.managePlans.useMutation()
	const managePlans = useCallback(async () => {
		const url = await createManagePlansMutation.mutateAsync()
		if (url) {
			location.href = url
		}
	}, [createManagePlansMutation])

	const managePaymentMethodsMutation = api.workspace.managePlan.useMutation()
	const handlePaymentMethodsClick = useCallback(async () => {
		try {
			const url = await managePaymentMethodsMutation.mutateAsync()
			if (url) {
				window.open(url, "_blank")
			}
		} catch (error) {
			console.error("Failed to open Stripe portal:", error)
			captureError(error, {
				domain: "billing",
				action: "open stripe portal",
				workspaceId: workspace?.id,
			})
		}
	}, [managePaymentMethodsMutation, workspace?.id])

	// A $0 unit amount (full discount, comped seat) is valid billing data, so
	// only a missing value hides the card.
	if (
		!trialInfo?.currentPeriodEnd ||
		trialInfo.unitAmount == null ||
		!trialInfo.currency ||
		trialInfo.quantity == null ||
		!trialInfo.interval
	) {
		return null
	}

	const {
		interval,
		quantity,
		unitAmount,
		currency,
		currentPeriodEnd,
		upcomingInvoiceTotal,
		isStartupProgram,
	} = trialInfo
	const intervalLabel = interval === "year" ? "user/yr" : "user/mo"
	const perUserPrice = formatMoney(unitAmount, currency)
	const totalPrice = formatMoney(
		upcomingInvoiceTotal ?? unitAmount * quantity,
		currency,
	)
	const planName = isStartupProgram
		? STARTUP_PROGRAM_DISPLAY_NAME
		: trialInfo.productName
			? trialInfo.productName
			: effectivePlan !== "free"
				? `${PLAN_DISPLAY_NAMES[effectivePlan] ?? effectivePlan} plan`
				: "Trial"

	return (
		<div className="rounded-[10px] bg-white p-4 shadow-card sm:-mx-3.5 dark:bg-gray-850 dark:shadow-card-dark">
			<div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between lg:gap-6">
				<div className="flex items-start justify-between gap-3 lg:items-center lg:justify-start">
					<div className="flex flex-col gap-1">
						<div className="flex items-center gap-2">
							<span className="whitespace-nowrap text-sm font-medium text-primary">
								{planName}
							</span>
							{isTrial ? (
								<Pill variant="blue">Trial</Pill>
							) : (
								<Pill variant="green">Current</Pill>
							)}
						</div>
						<div className="text-xs text-secondary">
							{perUserPrice} per {intervalLabel}
						</div>
					</div>
				</div>
				<div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:gap-6">
					<div className="-mx-4 grid grid-cols-2 gap-y-3 border-y border-gray-150 px-4 py-3 lg:mx-0 lg:flex lg:items-center lg:gap-10 lg:border-0 lg:px-0 lg:py-0 dark:border-white/[0.06]">
						{isTrial && (
							<div className="flex flex-col gap-1 lg:items-end">
								<span className="text-xs text-secondary">Trial period</span>
								<span className="text-sm text-primary">
									{daysLeft} {daysLeft === 1 ? "day" : "days"}
								</span>
							</div>
						)}
						<div className="flex flex-col gap-1 lg:items-end">
							<span className="text-xs text-secondary">Users</span>
							<span className="text-sm text-primary">{quantity}</span>
						</div>
						<div className="flex flex-col gap-1 lg:items-end">
							<span className="text-xs text-secondary">
								{cancelAtPeriodEnd ? "Scheduled to cancel on" : "Next renewal"}
							</span>
							<span className="text-sm text-primary">
								{cancelAtPeriodEnd ? (
									formatRenewalDate(currentPeriodEnd)
								) : (
									<>
										{totalPrice} on {formatRenewalDate(currentPeriodEnd)}
									</>
								)}
							</span>
						</div>
					</div>
					<div
						aria-hidden
						className="hidden h-8 w-px bg-gray-200 lg:block dark:bg-gray-700"
					/>
					<Button
						variant="outline"
						Icon={CreditCard}
						disabled={managePaymentMethodsMutation.isLoading}
						isLoading={managePaymentMethodsMutation.isLoading}
						onClick={handlePaymentMethodsClick}
						className="w-full lg:w-auto"
					>
						Payment methods
					</Button>
				</div>
			</div>

			{isStartupProgram && (
				<div className="mt-4 border-t pt-4 text-sm text-secondary">
					This workspace is on the Startup program, a{" "}
					{STARTUP_PROGRAM_CONTRACT_LENGTH_MONTHS}-month contract with the same
					entitlements as Scale. Contact us to change your tier.
				</div>
			)}

			{!isStartupProgram && !!needsUpgrade && (
				<div className="mt-4 flex items-center justify-between gap-2 border-t pt-4">
					<div className="flex items-center gap-2 text-sm text-secondary">
						<AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
						This is a legacy plan and you need to upgrade your current plan.
					</div>

					<Button
						isLoading={createManagePlansMutation.isLoading}
						onClick={managePlans}
					>
						Manage plan
					</Button>
				</div>
			)}
		</div>
	)
}

const AiUsageRow = () => {
	const { data: pricing } = api.workspace.aiResolutionPricing.useQuery()
	const { data: tokenUsage } = api.workspace.getAiTokenUsage.useQuery({})

	const usedResolutions = pricing?.usedThisMonth ?? 0
	const includedResolutions = pricing?.freeResolutions ?? 0

	const creditsSpent = tokenUsage?.spentThisMonthUsd ?? 0
	const creditsAvailable = tokenUsage
		? tokenUsage.capUsd === null
			? null
			: tokenUsage.freeAllowanceUsd + tokenUsage.capUsd
		: null
	const creditsText =
		creditsAvailable === null
			? `$${creditsSpent.toFixed(2)} credits`
			: `$${creditsSpent.toFixed(2)}/$${creditsAvailable.toFixed(2)} credits`

	return (
		<div className={cardClass}>
			<AiSettingsRow
				icon={Activity}
				title="AI usage and credits"
				description={`${usedResolutions}/${includedResolutions} resolutions · ${creditsText}`}
				link="/settings/ai/usage"
			/>
		</div>
	)
}

export function PlansSettingsPage() {
	const { data: activePlan } = api.workspace.activePlan.useQuery()
	const { data: trialInfo } = api.workspace.trialInfo.useQuery(undefined, {
		cacheTime: Infinity,
		staleTime: Infinity,
	})
	const { hasTrialEnded, paymentIssue, hadRecentCancellation } = useTrial()
	const { startTrial, isStartingTrial } = useStartTrial()
	const utils = api.useUtils()
	const navigate = useNavigate()
	const stripeSessionId = useSearch({
		strict: false,
		select: (s) => s.sessionId,
	})
	// Stripe redirects here with `?upgraded=true` after a successful checkout
	// (see create-checkout-session.ts). That's our "Paid subscription" signal.
	const upgraded = useSearch({ strict: false, select: (s) => s.upgraded })
	const paidConversionFiredRef = useRef(false)

	// trialInfo and activePlan are cached with staleTime: Infinity, so we must invalidate after returning from Stripe.
	useEffect(() => {
		if (!stripeSessionId) return
		void utils.workspace.trialInfo.invalidate()
		void utils.workspace.activePlan.invalidate()
		void navigate({
			to: ".",
			search: (prev) => ({ ...prev, sessionId: undefined }),
			replace: true,
		})
	}, [stripeSessionId, utils, navigate])

	// Refresh the cached plan/value data after returning from Stripe checkout so
	// the conversion below can read the new subscription's value.
	useEffect(() => {
		if (!upgraded) return
		void utils.workspace.trialInfo.invalidate()
		void utils.workspace.activePlan.invalidate()
	}, [upgraded, utils])

	// Fire the Google Ads "Paid subscription" conversion once, with the actual
	// subscription value when available, then drop the param so a reload can't
	// double-count.
	useEffect(() => {
		if (!upgraded) return
		if (paidConversionFiredRef.current) return
		if (!trialInfo) return
		paidConversionFiredRef.current = true

		const amountInCents =
			trialInfo.upcomingInvoiceTotal ??
			(trialInfo.unitAmount != null && trialInfo.quantity != null
				? trialInfo.unitAmount * trialInfo.quantity
				: undefined)
		trackPaidSubscriptionConversion({
			value: amountInCents != null ? amountInCents / 100 : undefined,
			currency: trialInfo.currency ?? undefined,
		})

		void navigate({
			to: ".",
			search: (prev) => ({ ...prev, upgraded: undefined }),
			replace: true,
		})
	}, [upgraded, trialInfo, navigate])

	const onStartupProgram = trialInfo?.isStartupProgram === true
	const showCurrentPlanCard =
		trialInfo?.isTrial === true ||
		(trialInfo?.plan && trialInfo.plan !== "free") ||
		(activePlan && activePlan !== "free")
	const effectivePlan = trialInfo?.plan ?? activePlan ?? "free"
	// The notice rides in the upgrade card's header, so it sits on the thing that
	// resolves it. Without that card there is nothing to fold into.
	const showsUpgradeCard = !onStartupProgram && !!nextUpgradeTier(effectivePlan)
	const trialEndedNotice = hasTrialEnded
		? "Your trial ended. Pick a plan to keep using Productlane."
		: undefined
	const restartTrialButton =
		hasTrialEnded && !hadRecentCancellation ? (
			<Button
				size="sm"
				isLoading={isStartingTrial}
				onClick={() => void startTrial()}
			>
				Restart 7-day trial
			</Button>
		) : undefined

	return (
		<div className="space-y-6 pb-12">
			<MetaInfo title="Billing" />

			<SettingsHeader
				subtitle="Manage your subscription and billing details."
				title="Billing"
				action={
					<Link to="/settings/plans/pricing">
						<Button variant="outline" iconRight={ArrowRight}>
							All plans
						</Button>
					</Link>
				}
			/>

			{!!paymentIssue && <PaymentIssueAlert issue={paymentIssue} />}

			{!!trialEndedNotice && !showsUpgradeCard && (
				<Alert
					variant="warning"
					header="Your trial has ended"
					className={SETTINGS_INSET_CLASS}
				>
					<div className="flex items-center gap-3">
						<span>Pick a plan below to continue using Productlane.</span>
						{!!restartTrialButton && (
							<span className="ml-auto flex-none">{restartTrialButton}</span>
						)}
					</div>
				</Alert>
			)}

			{showCurrentPlanCard && (
				<CurrentPlanCard activePlan={activePlan || "free"} />
			)}

			{!onStartupProgram && (
				<UpgradeCard
					activePlan={effectivePlan}
					notice={trialEndedNotice}
					noticeAction={restartTrialButton}
				/>
			)}

			<AiUsageRow />

			<BillingDetailsSection />

			<RecentInvoicesSection />
		</div>
	)
}
